import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, UploadSimple, Sparkle, X, ArrowLeft, Image as ImageIcon } from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import ShareSaveButton from '../components/ShareSaveButton';
import IdeaSuggester from '../components/IdeaSuggester';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_LABELS = {
  queued: 'Queued…',
  preparing: 'Analyzing your product…',
  generating_shots: 'Generating studio shots…',
  ready: 'Ready',
  failed: 'Failed',
};

export default function ProductShootPage() {
  const [styles, setStyles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sourceImage, setSourceImage] = useState(''); // /api/uploads/serve/...
  const [selectedStyle, setSelectedStyle] = useState('studio_model');
  const [mode, setMode] = useState('studio'); // 'studio' or 'background_swap'
  // Zero Friction: hide Mode + Variants behind Customise
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [extra, setExtra] = useState('');
  const [variants, setVariants] = useState(4);
  const [job, setJob] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/product-shoot/styles`).then(r => setStyles(r.data.styles || [])).catch(() => {});
    loadHistory();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/product-shoot/list`);
      setHistory(data.jobs || []);
    } catch {}
  };

  const uploadSource = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/uploads/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSourceImage(data.url);
      toast.success('Product photo uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const generate = async () => {
    if (!sourceImage) { toast.error('Upload a product photo first'); return; }
    try {
      const { data } = await axios.post(`${API}/api/product-shoot/generate`, {
        source_image_url: sourceImage, style: selectedStyle, extra_direction: extra, variants, mode,
      });
      setJob(data);
      pollJob(data.job_id);
      toast.success(`Generating ${variants} shots — usually 60–120s`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not start'); }
  };

  const pollJob = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/product-shoot/status/${id}`);
        setJob(data);
        if (data.status === 'ready' || data.status === 'failed') {
          clearInterval(pollRef.current); pollRef.current = null;
          if (data.status === 'ready') { toast.success('Your shots are ready!'); loadHistory(); }
          if (data.status === 'failed') toast.error(data.error || 'Generation failed — credits refunded');
        }
      } catch {}
    }, 4000);
  };

  const reset = () => { setJob(null); setSourceImage(''); setExtra(''); };

  const creditCost = variants * 5;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="product-shoot-page">
        <div>
          <h1 className="text-4xl sm:text-5xl font-['Instrument_Serif',serif]" style={{ color: '#f5f0e8' }}>Product Shoot Studio</h1>
          <p className="mt-2 text-base" style={{ color: 'rgba(245,240,232,0.5)' }}>
            Studio-quality product photos — from any photo you take.
          </p>
        </div>

        <BrandSettingsSummary fields={['brand','color']} />

        {!job ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT — upload + form */}
            <div className="space-y-5">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Upload your product photo</p>
                {sourceImage ? (
                  <div className="relative">
                    <img src={`${API}${sourceImage}`} alt="" className="w-full rounded-xl" style={{ maxHeight: 400, objectFit: 'contain', background: '#0f0f0f' }} />
                    <button onClick={() => setSourceImage('')} className="absolute top-2 right-2 p-2 rounded-lg bg-black/60 backdrop-blur" data-testid="ps-clear-upload">
                      <X size={14} style={{ color: '#fff' }} />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="ps-upload-btn"
                    className="w-full p-8 rounded-2xl flex flex-col items-center gap-3 transition-all disabled:opacity-50"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '2px dashed rgba(255,255,255,0.1)' }}>
                    <UploadSimple size={32} weight="duotone" style={{ color: '#c8f060' }} />
                    <div className="text-center">
                      <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>{uploading ? 'Uploading…' : 'Drop a product / garment photo'}</p>
                      <p className="text-xs mt-1" style={{ color: 'rgba(245,240,232,0.4)' }}>PNG, JPG, WEBP — up to 10MB</p>
                    </div>
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => uploadSource(e.target.files?.[0])} />
              </div>

              <button onClick={() => setShowAdvanced(v => !v)} data-testid="ps-customise-toggle"
                className="w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-semibold flex items-center justify-between"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
                <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {showAdvanced ? 'Hide customise' : 'Customise →'}
                </span>
                <span style={{ color: '#c8f060' }}>{showAdvanced ? '−' : '+'}</span>
              </button>

              {showAdvanced && (
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Mode</p>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setMode('studio')} data-testid="ps-mode-studio"
                    className="p-3 rounded-xl text-left transition-all"
                    style={{
                      background: mode === 'studio' ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.03)',
                      border: mode === 'studio' ? '1px solid rgba(200,240,96,0.3)' : '1px solid rgba(255,255,255,0.05)',
                    }}>
                    <p className="text-xs font-semibold" style={{ color: mode === 'studio' ? '#c8f060' : '#f5f0e8' }}>Studio shots</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>Recreate the product in a new scene with a model</p>
                  </button>
                  <button onClick={() => setMode('background_swap')} data-testid="ps-mode-bg-swap"
                    className="p-3 rounded-xl text-left transition-all"
                    style={{
                      background: mode === 'background_swap' ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.03)',
                      border: mode === 'background_swap' ? '1px solid rgba(200,240,96,0.3)' : '1px solid rgba(255,255,255,0.05)',
                    }}>
                    <p className="text-xs font-semibold" style={{ color: mode === 'background_swap' ? '#c8f060' : '#f5f0e8' }}>Background swap</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>Keep the exact product, change only the scene</p>
                  </button>
                </div>
              </div>

              )}

              <div>
                <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Pick a shoot style</p>
                <div className="grid grid-cols-2 gap-2">
                  {styles.map(s => (
                    <button key={s.id} onClick={() => setSelectedStyle(s.id)} data-testid={`ps-style-${s.id}`}
                      className="p-3 rounded-xl text-left transition-all"
                      style={{
                        background: selectedStyle === s.id ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.03)',
                        border: selectedStyle === s.id ? '1px solid rgba(200,240,96,0.3)' : '1px solid rgba(255,255,255,0.05)',
                      }}>
                      <p className="text-xs font-semibold" style={{ color: selectedStyle === s.id ? '#c8f060' : '#f5f0e8' }}>{s.label}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>{s.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(245,240,232,0.4)' }}>Extra direction (optional)</p>
                  <IdeaSuggester creator="product_shoot" extraContext={selectedStyle}
                    onPick={(idea) => {
                      if (idea.scene_description) setExtra(idea.scene_description);
                      else if (idea.outline) setExtra(idea.outline);
                      if (idea.shoot_style) setSelectedStyle(idea.shoot_style);
                    }} compact label="Suggest" />
                </div>
                <textarea value={extra} onChange={e => setExtra(e.target.value)} rows={2} data-testid="ps-extra"
                  placeholder="e.g. tropical beach setting, model in profile, focus on the texture"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl px-3 py-2 text-sm resize-none" style={{ color: '#f5f0e8' }} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(245,240,232,0.4)' }}>Shots to generate</p>
                  <div className="flex gap-1 mt-1.5">
                    {[1, 2, 3, 4].map(n => (
                      <button key={n} onClick={() => setVariants(n)} data-testid={`ps-variants-${n}`}
                        className="w-9 h-9 rounded-lg text-xs font-semibold"
                        style={{
                          background: variants === n ? '#c8f060' : 'rgba(255,255,255,0.04)',
                          color: variants === n ? '#080808' : '#f5f0e8',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}>{n}</button>
                    ))}
                  </div>
                </div>
                <button onClick={generate} disabled={!sourceImage} data-testid="ps-generate"
                  className="px-5 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
                  style={{ background: '#c8f060', color: '#080808' }}>
                  <Sparkle size={14} weight="fill" /> Create &middot; {creditCost} credits
                </button>
              </div>
            </div>

            {/* RIGHT — history */}
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Recent shoots</p>
              {history.length === 0 ? (
                <div className="p-8 rounded-2xl text-center" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <Camera size={32} weight="duotone" style={{ color: 'rgba(200,240,96,0.4)' }} className="mx-auto mb-3" />
                  <p className="text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>No shoots yet — your first one will appear here.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {history.slice(0, 6).map(h => (
                    <button key={h.job_id} onClick={() => setJob(h)} data-testid={`ps-history-${h.job_id}`}
                      className="aspect-square rounded-xl overflow-hidden relative" style={{ background: '#141414' }}>
                      {h.shots?.[0]?.image_url ? (
                        <img src={`${API}${h.shots[0].image_url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ color: 'rgba(245,240,232,0.3)' }}>
                          <ImageIcon size={24} />
                        </div>
                      )}
                      <p className="absolute bottom-0 left-0 right-0 text-[10px] px-2 py-1 truncate" style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>{h.style_label}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <button onClick={reset} className="flex items-center gap-2 text-sm mb-4" data-testid="ps-back" style={{ color: 'rgba(245,240,232,0.5)' }}>
              <ArrowLeft size={16} /> New shoot
            </button>

            {job.status === 'generating' || job.status === 'queued' ? (
              <div className="p-12 rounded-2xl text-center" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'rgba(200,240,96,0.2)', borderTopColor: '#c8f060' }} />
                <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>{STEP_LABELS[job.step] || 'Working…'}</p>
                <div className="max-w-xs mx-auto mt-3">
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div className="h-full transition-all duration-500" style={{ width: `${job.progress || 0}%`, background: '#c8f060' }} />
                  </div>
                  <p className="text-xs mt-2" style={{ color: 'rgba(245,240,232,0.4)' }}>{job.progress || 0}%</p>
                </div>
              </div>
            ) : job.status === 'failed' ? (
              <div className="p-12 rounded-2xl text-center" style={{ background: '#141414', border: '1px solid rgba(255,68,68,0.15)' }}>
                <p className="text-sm font-semibold" style={{ color: '#ff6b6b' }}>Generation failed</p>
                <p className="text-xs mt-1" style={{ color: 'rgba(245,240,232,0.5)' }}>{job.error || 'Try a different reference image or style.'} Credits refunded.</p>
              </div>
            ) : (
              <>
                {job.effective_style && job.effective_style !== job.style && (
                  <div className="mb-3 px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.15)', color: 'rgba(200,240,96,0.9)' }}>
                    <strong>Style auto-tuned:</strong> Your product looks like {job.classification?.category} ({job.classification?.subtype}), so we used <strong>{job.effective_style_label}</strong> instead of {job.style_label} for a better fit.
                  </div>
                )}
                <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: 'rgba(245,240,232,0.4)' }}>
                  {job.shots?.length || 0} shots · {job.effective_style_label || job.style_label}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {(job.shots || []).map(s => (
                    <button key={s.index} onClick={() => setPreview(s)} data-testid={`ps-shot-${s.index}`}
                      className="aspect-square rounded-xl overflow-hidden group relative" style={{ background: '#141414' }}>
                      <img src={`${API}${s.image_url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <AnimatePresence>
          {preview && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setPreview(null)}
              className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} data-testid="ps-preview">
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}
                className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <img src={`${API}${preview.image_url}`} alt="" className="w-full" />
                <div className="p-4 flex items-center justify-between gap-3">
                  <p className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>Shot {preview.index + 1}</p>
                  <ShareSaveButton url={preview.image_url} filename={`product-shoot-${preview.index + 1}.png`}
                    title="My product shot" text="Made with ScalitOS" mimeType="image/png" dataTestId={`ps-preview-${preview.index}`} />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
